import { supabase } from "@/integrations/supabase/client";

/**
 * Sync design disciplines to a document's junction table by replacing all existing disciplines.
 * Follows the same pattern as syncCategoriesToM2M in categoryHelpers.ts
 *
 * @param documentId - The document ID
 * @param disciplineIds - Array of discipline IDs to set
 * @param userId - User ID for audit fields
 */
export async function syncDisciplinesToDocument(
  documentId: number,
  disciplineIds: number[],
  userId: number
): Promise<void> {
  // Remove all existing disciplines for this document
  const { error: deleteError } = await supabase
    .from("document_design_disciplines")
    .delete()
    .eq("document_id", documentId);

  if (deleteError) {
    throw new Error(`Failed to remove existing disciplines: ${deleteError.message}`);
  }

  // Insert new disciplines if any
  if (disciplineIds.length > 0) {
    const rows = disciplineIds.map((disciplineId) => ({
      document_id: documentId,
      discipline_id: disciplineId,
      created_by: userId,
      modified_by: userId,
    }));

    const { error: insertError } = await supabase.from("document_design_disciplines").insert(rows);

    if (insertError) {
      throw new Error(`Failed to insert disciplines: ${insertError.message}`);
    }
  }
}

/**
 * Sync design stages to a document's junction table by replacing all existing stages.
 * Follows the same pattern as syncCategoriesToM2M in categoryHelpers.ts
 *
 * @param documentId - The document ID
 * @param stageIds - Array of stage IDs to set
 * @param userId - User ID for audit fields
 */
export async function syncStagesToDocument(
  documentId: number,
  stageIds: number[],
  userId: number
): Promise<void> {
  // Remove all existing stages for this document
  const { error: deleteError } = await supabase
    .from("document_design_stages")
    .delete()
    .eq("document_id", documentId);

  if (deleteError) {
    throw new Error(`Failed to remove existing stages: ${deleteError.message}`);
  }

  // Insert new stages if any
  if (stageIds.length > 0) {
    const rows = stageIds.map((stageId) => ({
      document_id: documentId,
      stage_id: stageId,
      created_by: userId,
      modified_by: userId,
    }));

    const { error: insertError } = await supabase.from("document_design_stages").insert(rows);

    if (insertError) {
      throw new Error(`Failed to insert stages: ${insertError.message}`);
    }
  }
}

/**
 * Add a single discipline to a document (e.g., for undo after removal)
 * @param documentId - The document ID
 * @param disciplineId - The discipline ID to add
 */
export async function addDisciplineToDocument(
  documentId: number,
  disciplineId: number,
  userId: number
): Promise<void> {
  const { error } = await supabase.from("document_design_disciplines").insert({
    document_id: documentId,
    discipline_id: disciplineId,
    created_by: userId,
    modified_by: userId,
  });
  if (error) {
    throw new Error(`Failed to add discipline: ${error.message}`);
  }
}

/**
 * Add a single stage to a document (e.g., for undo after removal)
 * @param documentId - The document ID
 * @param stageId - The stage ID to add
 * @param userId - User ID for audit fields
 */
export async function addStageToDocument(
  documentId: number,
  stageId: number,
  userId: number
): Promise<void> {
  const { error } = await supabase.from("document_design_stages").insert({
    document_id: documentId,
    stage_id: stageId,
    created_by: userId,
    modified_by: userId,
  });
  if (error) {
    throw new Error(`Failed to add stage: ${error.message}`);
  }
}

/**
 * Remove a single discipline from a document
 * @param documentId - The document ID
 * @param disciplineId - The discipline ID to remove
 */
export async function removeDisciplineFromDocument(
  documentId: number,
  disciplineId: number
): Promise<void> {
  const { error } = await supabase
    .from("document_design_disciplines")
    .delete()
    .eq("document_id", documentId)
    .eq("discipline_id", disciplineId);

  if (error) {
    throw new Error(`Failed to remove discipline: ${error.message}`);
  }
}

/**
 * Remove a single stage from a document
 * @param documentId - The document ID
 * @param stageId - The stage ID to remove
 */
export async function removeStageFromDocument(documentId: number, stageId: number): Promise<void> {
  const { error } = await supabase
    .from("document_design_stages")
    .delete()
    .eq("document_id", documentId)
    .eq("stage_id", stageId);

  if (error) {
    throw new Error(`Failed to remove stage: ${error.message}`);
  }
}

/**
 * Sync categories to a document's junction table by replacing all existing categories.
 * This is a convenience re-export with ID-based interface to match disciplines/stages.
 *
 * @param documentId - The document ID
 * @param categoryIds - Array of category IDs to set
 * @param userId - User ID for audit fields
 */
export async function syncCategoriesToDocument(
  documentId: number,
  categoryIds: number[],
  userId: number
): Promise<void> {
  // Remove all existing categories for this document
  const { error: deleteError } = await supabase
    .from("document_document_categories")
    .delete()
    .eq("document_id", documentId);

  if (deleteError) {
    throw new Error(`Failed to remove existing categories: ${deleteError.message}`);
  }

  // Insert new categories if any
  if (categoryIds.length > 0) {
    const rows = categoryIds.map((categoryId) => ({
      document_id: documentId,
      category_id: categoryId,
      created_by: userId,
      modified_by: userId,
    }));

    const { error: insertError } = await supabase.from("document_document_categories").insert(rows);

    if (insertError) {
      throw new Error(`Failed to insert categories: ${insertError.message}`);
    }
  }
}
